/**
 * focus-trap.js — 跨檔共用的 focus-trap primitive（Phase A batch-4 A1 util 去重收斂）
 *
 * 收斂全站 4 份各自實作的 focus-trap selector + getFocusable + Tab 循環邏輯為一份：
 *   - modal.js（基準，最完整）：6-token selector（排 [type=hidden]、含 select/textarea）+
 *     keep-active-el-if-hidden filter。
 *   - admin-topnav.js（modal 的近複本）：同 selector，但 filter 漏 keep-active-el。
 *   - site-nav.js（drawer）：6-token 但漏 input[type=hidden] 守衛。
 *   - chat-dialog.js：4-token，漏 select / textarea（overlay 內 <select> 會跳出 trap = 真 a11y 缺陷）+
 *     漏 hidden 守衛；但其 Tab handler 多了「焦點逃出 container 時拉回」的逃逸復原守衛。
 *
 * Canonical 決策（依 audit/phase-a-convention-spec.md §1）：
 *   - selector = modal.js 的 6-token superset（其餘三者皆其嚴格子集，子集漏覆蓋）。
 *   - getFocusable filter = modal.js 的 keep-active-el-if-hidden（焦點控制項過場中算 hidden 時不丟，
 *     避免焦點流失）。
 *   - trapTab 含 chat-dialog 的「焦點逃出 container → 拉回首/末」逃逸復原守衛（superset，
 *     對 modal/admin-topnav/site-nav 是 a11y 強化，見各遷移點註解）。
 *
 * 三者皆純函式、無 DOM 副作用、不 auto-init；由各 consumer import 使用。
 */

/**
 * Canonical 可聚焦元素 selector（modal.js 的 6-token superset，逐字）。
 * 匯出供 caller 自行 query 時重用（避免再各自寫一份 selector 字串）。
 */
export const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * 取 container 內「可見、依 DOM 順序」的可聚焦元素。
 *
 * filter = modal.js 的 keep-active-el-if-hidden：濾掉無 client rect 的元素
 * （offsetParent === null，即 display:none / 脫離排版），但**保留當前 document.activeElement**
 * （即使它此刻算 hidden）—— 防止焦點控制項在進/退場過場中被排除而導致焦點流失。
 *
 * @param {HTMLElement} container 要在其內找可聚焦元素的容器（modal panel / drawer / dialog）。
 * @returns {HTMLElement[]} 依 DOM 順序的可聚焦元素陣列。
 */
export function getFocusable(container) {
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement
  );
}

/**
 * Tab / Shift+Tab 循環處理（首 ↔ 末 wrap）。在 container 的 keydown 監聽中對 Tab 事件呼叫。
 *
 * 行為：
 *   - container 內無可聚焦元素 → preventDefault（焦點不漏到背景頁面）。
 *   - Shift+Tab 且焦點在首元素（或焦點已逃出 container）→ 跳到末元素。
 *   - Tab 且焦點在末元素（或焦點已逃出 container）→ 回到首元素。
 *
 * 「焦點已逃出 container」（!container.contains(active)）的逃逸復原守衛源自 chat-dialog.js：
 *   若焦點因任何原因落在 trap 之外，下一次 Tab/Shift+Tab 會把它拉回 trap 內，強化鍵盤陷阱穩固性。
 *
 * @param {KeyboardEvent} event Tab 鍵的 keydown 事件（caller 已確認 event.key === 'Tab'）。
 * @param {HTMLElement} container 焦點要鎖在其內的容器。
 */
export function trapTab(event, container) {
  const focusables = getFocusable(container);
  if (focusables.length === 0) {
    event.preventDefault();
    return;
  }

  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement;
  const outside = !container.contains(active);

  if (event.shiftKey) {
    if (active === first || outside) {
      event.preventDefault();
      last.focus();
    }
  } else if (active === last || outside) {
    event.preventDefault();
    first.focus();
  }
}
